import React, { useEffect } from "react";
import {
    Navbar,
    NavbarBrand,
    NavbarContent,
    NavbarMenuToggle,
    NavbarMenu,
    Image,
    Kbd,
    Button,
    Link,
    NavbarItem,
    Accordion,
    AccordionItem,
    Avatar,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
} from "@nextui-org/react";
import nekoLogo from '../../../static/logos/nekoLogo.png';
import { SearchIcon } from "../../../static/icons/search";
import { useLocation } from "react-router-dom";
import { useKBar } from "kbar";
import { FaChevronDown } from "react-icons/fa";
import PageSelector from "../../docs/pageSelector";
import { pages } from "../../docs/pages/pages";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "../../../config/api";
import NotificationBell from "../notificationBell";

function absoluteUpload(url) {
    if (!url) return null;
    return url.startsWith("http") ? url : `${API_URL}${url}`;
}

export default function NavBar({ selectedLink, setSelectedLink }) {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [version, setVersion] = React.useState(null);
    const { user, logout, isStaff } = useAuth();
    const menuItems = [
        { name: 'Docs', link: '/docs', hasAccordion: true, accordion: <PageSelector selectedLink={selectedLink} setSelectedLink={(link) => { setSelectedLink(link); setIsMenuOpen(false); }} pages={pages} isNavBar={true} onSelect={() => setIsMenuOpen(false)} /> },
        { name: 'Forum', link: '/forum' },
        { name: 'Blog', link: '/blog' },
        { name: 'Status', link: '/status' },
        { name: 'Changelog', link: '/changelog' },
        { name: 'Github', link: 'https://github.com/Neko-CLI/Neko-CLI-WebSite' },
        { name: 'Discord', link: 'https://discord.gg/5Vax43BYd' },
        { name: 'Packages', link: '/packages' },
        { name: 'APIs', link: '/apis' },
        { name: 'Learning', link: '/learning' },
        { name: 'Enterprise', link: '/enterprise' },
        { name: 'Support', link: '/support' },
        { name: 'Download App', link: '/download' }
    ];

    // On desktop the full 13-item list no longer fits inline next to the
    // search bar and the login/avatar controls -- it used to wrap/overflow
    // and shove login/register/avatar off screen. Only a handful of
    // primary links stay inline; everything else moves into a "More"
    // dropdown. The mobile drawer (NavbarMenu) still lists every item, so
    // nothing is actually removed, just reorganized for narrower widths.
    const PRIMARY_LINK_NAMES = ['Docs', 'Packages', 'Blog', 'Forum', 'Download App'];
    const primaryMenuItems = menuItems.filter((x) => PRIMARY_LINK_NAMES.includes(x.name));
    const moreMenuItems = menuItems.filter((x) => !PRIMARY_LINK_NAMES.includes(x.name));

    const handleLogout = async () => {
        await logout();
        window.location.href = "/";
    };
    useEffect(() => {
        const fetchVersion = async () => {
            try {
                const info = await fetch(
                    "https://registry.npmjs.org/neko-cli/latest"
                ).then((res) => res.json());
                setVersion(info.version);
            } catch (error) {
                console.error("Error fetching Neko-CLI version:", error);
                setVersion('N/A');
            }
        };
        fetchVersion();
    }, []);
    const location = useLocation();
    const { query } = useKBar();
    return (
        <Navbar isMenuOpen={isMenuOpen} onMenuOpenChange={setIsMenuOpen}
            onMenuOpenChange={setIsMenuOpen}
            classNames={{
                wrapper: [
                    "max-w-[1440px]",
                    "w-full"
                ],
                item: [
                    "text-foreground",
                    "flex",
                    "relative",
                    "h-full",
                    "items-center",
                    "data-[active=true]:text-primary",
                    "data-[active=true]:after:content-['']",
                    "data-[active=true]:after:absolute",
                    "data-[active=true]:after:bottom-4",
                    "data-[active=true]:after:left-0",
                    "data-[active=true]:after:right-0",
                    "data-[active=true]:after:h-[2px]",
                    "data-[active=true]:after:rounded-[2px]",
                    "data-[active=true]:after:bg-gradient-to-r",
                    "data-[active=true]:after:from-pride-red",
                    "data-[active=true]:after:via-pride-blue",
                    "data-[active=true]:after:to-pride-purple",
                ]
            }}
            position="sticky"
            isMenuOpen={isMenuOpen}
            onMenuOpenChange={setIsMenuOpen}
        >
            <NavbarContent>
                <NavbarMenuToggle
                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    className="lg:hidden"
                />
                <NavbarBrand>
                    <Link href="/" className="text-foreground flex items-center gap-3">
                        <Image
                            alt="NekoCLI Logo"
                            src={nekoLogo}
                            width={36}
                            height={36}
                            className="hover:scale-110 transition-transform object-contain flex-shrink-0"
                        />
                        <div className="flex flex-col gap-0">
                            <span className="font-bold text-inherit text-lg bg-gradient-to-r from-pride-pink via-pride-yellow to-pride-lightblue bg-clip-text text-transparent">
                                Neko-CLI
                            </span>
                            {version && <span className="text-inherit text-small -mt-2">v{version}</span>}
                        </div>
                    </Link>
                </NavbarBrand>
            </NavbarContent>
            <NavbarMenu className="flex flex-col gap-6" onClick={() => setIsMenuOpen(false)}>
                {
                    menuItems.map((x, index) => (
                        <NavbarItem isActive={location.pathname.includes(x.link)} key={index} className="h-auto w-fit after:hidden">
                            {
                                x.hasAccordion ? (
                                    <Accordion
                                        
                                        isCompact
                                        showDivider={false}
                                        selectionMode="multiple"
                                        className="p-0"
                                        itemClasses={{
                                            trigger: ["w-auto", "p-0"],
                                            indicator: ["text-foreground"],
                                            title: ["font-normal"]
                                        }}
                                    >
                                        <AccordionItem key="0" aria-label={x.name} title={x.name}>
                                            <div onClick={() => setIsMenuOpen(false)}>{x.accordion}</div>
                                        </AccordionItem>
                                    </Accordion>
                                ) : (
                                    <Link href={x.link} className="text-[unset]" onClick={() => setIsMenuOpen(false)}>
                                        {x.name}
                                    </Link>
                                )
                            }
                        </NavbarItem>
                    ))
                }
                {/* Mobile-only auth actions: on small screens the login/signup buttons live
                    here, inside the drawer, instead of in the fixed top bar (see below). */}
                {!user && (
                    <div className="flex flex-col gap-3 pt-4 mt-2 border-t border-default-100 lg:hidden">
                        <Button as={Link} href="/login" variant="flat" onClick={() => setIsMenuOpen(false)}>
                            Log in
                        </Button>
                        <Button as={Link} href="/register" color="primary" className="font-medium" onClick={() => setIsMenuOpen(false)}>
                            Sign up
                        </Button>
                    </div>
                )}
            </NavbarMenu>
            <NavbarContent as="div" className="items-center flex gap-2 sm:gap-4 lg:gap-8 shrink-0" justify="end">
                <NavbarContent className="hidden lg:flex gap-6 shrink-0" justify="end">
                    {
                        primaryMenuItems.map((x, index) => (
                            <NavbarItem isActive={location.pathname.includes(x.link)} key={index}>
                                <Link href={x.link} className="text-[unset]" onClick={() => setIsMenuOpen(false)}>
                                    {x.name}
                                </Link>
                            </NavbarItem>
                        ))
                    }
                    <Dropdown placement="bottom-start">
                        <NavbarItem>
                            <DropdownTrigger>
                                <button className="flex items-center gap-1 text-[unset] bg-transparent p-0 cursor-pointer">
                                    More
                                    <FaChevronDown size={10} />
                                </button>
                            </DropdownTrigger>
                        </NavbarItem>
                        <DropdownMenu aria-label="More links">
                            {moreMenuItems.map((x) => (
                                <DropdownItem
                                    key={x.link}
                                    href={x.link}
                                    target={x.link.startsWith('http') ? '_blank' : undefined}
                                    rel={x.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                                >
                                    {x.name}
                                </DropdownItem>
                            ))}
                        </DropdownMenu>
                    </Dropdown>
                </NavbarContent>
                <Button
                    className="flex flex-row items-center gap-2 border-1 border-x-1.5 p-2 h-fit pl-2.5 text-foreground-500 hover:bg-gradient-to-r hover:from-pride-red hover:via-pride-blue hover:to-pride-purple hover:border-pride-purple transition-all duration-300"
                    variant="bordered"
                    radius="full"
                    isIconOnly={false}
                    onClick={() => query.toggle()}
                >
                    <SearchIcon size={16} />
                    <span className="mr-2 pr-2 hidden sm:inline">Search</span>
                    <Kbd keys={["command"]} className="rounded-full px-2 bg-content1 hidden sm:inline-flex">k</Kbd>
                </Button>

                {user ? (
                    <>
                    <NotificationBell />
                    <Dropdown placement="bottom-end">
                        <DropdownTrigger>
                            <Avatar
                                as="button"
                                src={absoluteUpload(user.avatar_url)}
                                name={user.nickname || user.email}
                                size="sm"
                                className="cursor-pointer transition-transform"
                            />
                        </DropdownTrigger>
                        <DropdownMenu aria-label="Account" onAction={(key) => {
                            if (key === "logout") return handleLogout();
                            window.location.href = key;
                        }}>
                            <DropdownItem key={user.nickname ? `/@${user.nickname}` : "/settings/profile"} className="h-12" textValue="account">
                                <span className="font-semibold">{user.nickname ? `@${user.nickname}` : user.email}</span>
                                <br />
                                <span className="text-tiny text-foreground-400">{user.role}</span>
                            </DropdownItem>
                            <DropdownItem key="/settings/profile">Profile settings</DropdownItem>
                            <DropdownItem key="/blog/new">Write post</DropdownItem>
                            {isStaff ? (
                                <DropdownItem key="/admin/users" color="danger">Admin panel</DropdownItem>
                            ) : null}
                            <DropdownItem key="logout" color="danger">Log out</DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                    </>
                ) : (
                    <>
                        {/* Desktop: full Log in / Sign up pair. */}
                        <div className="hidden lg:flex items-center gap-2">
                            <Button as={Link} href="/login" variant="light" size="sm">
                                Log in
                            </Button>
                            <Button as={Link} href="/register" color="primary" size="sm" className="font-medium">
                                Sign up
                            </Button>
                        </div>
                        {/* Mobile/tablet: a compact Log in button visible directly in the
                            top bar, so it's not hidden away inside the hamburger drawer
                            (the full pair still lives in the drawer too, see NavbarMenu
                            above). */}
                        <Button
                            as={Link}
                            href="/login"
                            color="primary"
                            size="sm"
                            radius="full"
                            className="font-medium lg:hidden"
                        >
                            Log in
                        </Button>
                    </>
                )}
            </NavbarContent>
        </Navbar>
    );
}
